import {
  Button,
  DatePicker,
  Drawer,
  Form,
  Input,
  InputNumber,
  message,
  Radio,
  Select,
  Space,
  Switch,
} from 'antd';
import axios from 'axios';
import { FETCHING_STATUS, REQUEST_LOADING } from 'constants/alerts';
import { CONTENT_SPAN, LABEL_SPAN } from 'constants/table';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { BCCService } from 'services/BCCService';
import { EmployeesService } from 'services/EmployeesService';
import { SectionTitle } from 'ui/SectionTitle';
import { fullWidthStyle } from 'utils';
import { getBirthdayFromIIN } from 'utils/birthday';
import { thousandFormatter } from 'utils/forms/formatter';
import { isValidIin } from 'utils/forms/iinValidation';
import {
  iinRule,
  onlyNumberRule,
  requiredRule,
} from 'utils/forms/validationRules';
import {
  calculationTypeList,
  citizenshipList,
  disabilityList,
  workWeekList,
} from '../datasource';

function EmployeesForm({
  isActive,
  initialValues,
  clientId,
  onClose,
  onSuccess,
  isIP,
  hasSupervisor,
}) {
  const [form] = Form.useForm();
  const [isOpenPensionerField, showPensionerField] = useState(false);
  const [countries, setCountries] = useState([]);
  const [citizenship, setCitizenship] = useState('resident');

  const getCountriesCode = async () => {
    try {
      const res = await axios.get('/country-code/');
      if (res.data) {
        setCountries(res.data);
        return res.data;
      }
    } catch (error) {
      message.warning('Не удалось получить коды стран');
    }
  };

  const isEditing = initialValues !== null;
  const supervisorDisabled = !isEditing && isIP && hasSupervisor;
  const employeeDisabled = !isEditing && isIP && !hasSupervisor;

  useEffect(() => {
    if (initialValues) {
      const {
        iin,
        first_name,
        last_name,
        patronymic,
        birthday,
        citizenship,
        position,
        custom_position,
        earn,
        current_salary,
        work_week,
        calculation_type,
        disability,
        pensioner,
        pensioner_date,
        date,
        tax_deduction,
        country_of_residency,
      } = initialValues;

      form.setFieldsValue({
        iin,
        first_name,
        last_name,
        patronymic,
        birthday: dayjs(birthday),
        citizenship,
        position,
        custom_position,
        earn,
        current_salary,
        work_week,
        calculation_type,
        disability,
        pensioner,
        pensioner_date: pensioner ? dayjs(pensioner_date) : undefined,
        date: dayjs(date),
        tax_deduction,
        country_of_residency,
      });

      setCitizenship(citizenship);

      if (pensioner) {
        showPensionerField(true);
      }
    } else {
      resetFields();
    }
  }, [initialValues]);

  useEffect(() => {
    if (isActive && !isEditing) {
      if (isIP && hasSupervisor) {
        form.setFieldsValue({ position: 'employee' });
      } else if (isIP && !hasSupervisor) {
        form.setFieldsValue({ position: 'supervisor' });
      }
    }
  }, [isActive, isEditing]);

  const handleSubmit = (data) => {
    const payload = {
      ...data,
      client_id: clientId,
      birthday: data?.birthday.format('YYYY-MM-DD'),
      date: data?.date.format('YYYY-MM-DD'),
      pensioner_date: data?.pensioner_date
        ? data.pensioner_date.format('YYYY-MM-DD')
        : null,
    };

    if (isEditing) {
      handleUpdate(payload, initialValues.id);
    } else {
      handleCreate(payload);
    }
  };

  const handleConfirm = () => {
    resetFields();
    onSuccess();
  };

  const resetFields = () => {
    form.resetFields();
    showPensionerField(false);
  };

  const handleCreate = async (payload) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isCreated = await EmployeesService.createEmployeeOfClient(payload);

      if (isCreated) {
        handleConfirm();
        message.success('Сотрудник успешно добавлен');
      }
    } catch (err) {
      message.error('Не удалось добавить сотрудника');
    } finally {
      hide();
    }
  };

  const handleUpdate = async (payload, workerId) => {
    const hide = message.loading(REQUEST_LOADING);

    try {
      const isUpdated = await EmployeesService.updateEmployeeOfClient(
        payload,
        workerId
      );

      if (isUpdated) {
        handleConfirm();
        message.success('Сотрудник успешно отредактирован');
      }
    } catch (err) {
      message.error('Не удалось отредактировать сотрудника');
    } finally {
      hide();
    }
  };

  const handleIINChange = async (event) => {
    const iin = event.target.value;
    const canFetchInfo = isValidIin(iin);

    if (canFetchInfo) {
      const hide = message.loading(FETCHING_STATUS);

      try {
        const birthdayDate = getBirthdayFromIIN(iin);
        const { first_name, last_name, father_name } =
          await BCCService.getPersonInfoByIIN(iin);

        form.setFieldsValue({
          first_name,
          last_name,
          patronymic: father_name,
          birthday: dayjs(birthdayDate),
        });

        hide();
      } catch (err) {
        hide();
      }
    }
  };

  useEffect(() => {
    getCountriesCode();
  }, []);

  return (
    <Drawer
      title={isEditing ? 'Редактирование сотрудника' : 'Создание сотрудника'}
      width="50%"
      onClose={() => {
        onClose();
        setCitizenship('resident');
      }}
      visible={isActive}
      getContainer={false}
    >
      <Form
        form={form}
        onFinish={handleSubmit}
        layout="horizontal"
        labelCol={{
          span: LABEL_SPAN,
        }}
        wrapperCol={{ span: CONTENT_SPAN }}
        labelAlign="right"
        initialValues={{
          disability: 'notDisabled',
          citizenship: 'resident',
          calculation_type: 'dailySalary',
          work_week: 5,
          tax_deduction: true,
          vsms_substract_ipn: true,
          pensioner: false,
        }}
      >
        <SectionTitle>Личные данные</SectionTitle>

        <Form.Item
          name="iin"
          label="ИИН"
          rules={[requiredRule, iinRule, onlyNumberRule]}
        >
          <Input
            allowClear
            maxLength={12}
            placeholder="Введите ИИН"
            onChange={handleIINChange}
          />
        </Form.Item>

        <Form.Item name="first_name" label="Имя" rules={[requiredRule]}>
          <Input placeholder="Имя" allowClear />
        </Form.Item>

        <Form.Item name="last_name" label="Фамилия" rules={[requiredRule]}>
          <Input placeholder="Фамилия" allowClear />
        </Form.Item>

        <Form.Item name="patronymic" label="Отчество">
          <Input placeholder="Отчество" allowClear />
        </Form.Item>

        <Form.Item name="birthday" label="Дата рождения" rules={[requiredRule]}>
          <DatePicker
            format="DD.MM.YYYY"
            allowClear
            style={{ width: '100%' }}
          />
        </Form.Item>

        <SectionTitle>Должность</SectionTitle>

        <Form.Item
          name="position"
          label="Тип"
          rules={[requiredRule]}
          tooltip={
            supervisorDisabled
              ? 'У данного клиента уже добавлен руководитель'
              : undefined
          }
        >
          <Radio.Group>
            <Space direction="vertical">
              <Radio value="supervisor" disabled={supervisorDisabled}>
                Руководитель
              </Radio>
              <Radio value="employee" disabled={employeeDisabled}>
                Работник
              </Radio>
            </Space>
          </Radio.Group>
        </Form.Item>

        <Form.Item name="custom_position" label="Должность">
          <Input placeholder="Введите должность" allowClear />
        </Form.Item>

        <SectionTitle>Зарплата</SectionTitle>

        <Form.Item
          name="current_salary"
          label="Размер текущей заработной платы"
          rules={[
            requiredRule,
            {
              validator: (_, value) => {
                if (value < 60000 || value === null) {
                  return Promise.reject(false);
                }

                return Promise.resolve(true);
              },
              message: 'Нужно ввести мининум 60000',
            },
          ]}
        >
          <InputNumber
            addonAfter="KZT"
            controls={false}
            formatter={thousandFormatter}
            placeholder="Размер текущей заработной платы"
            style={fullWidthStyle}
            disabled={isEditing}
          />
        </Form.Item>

        <Form.Item
          name="earn"
          label="Размер начальной заработной платы"
          rules={[requiredRule]}
        >
          <InputNumber
            addonAfter="KZT"
            controls={false}
            formatter={thousandFormatter}
            placeholder="Размер начальной заработной платы"
            style={fullWidthStyle}
            disabled={isEditing}
          />
        </Form.Item>

        <Form.Item name="date" label="Дата приема" rules={[requiredRule]}>
          <DatePicker format="DD.MM.YYYY" allowClear style={fullWidthStyle} />
        </Form.Item>

        <Form.Item
          name="calculation_type"
          label="Вид расчета"
          rules={[requiredRule]}
        >
          <Select placeholder="Вид расчета" allowClear>
            {calculationTypeList.map(({ value, text }) => (
              <Select.Option key={value} value={value}>
                {text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          name="work_week"
          label="Рабочая неделя"
          rules={[requiredRule]}
        >
          <Select placeholder="Рабочая неделя" allowClear>
            {workWeekList.map(({ value, text }) => (
              <Select.Option key={value} value={value}>
                {text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <SectionTitle>Дополнительно</SectionTitle>

        <Form.Item
          name="citizenship"
          label="Гражданство"
          rules={[requiredRule]}
        >
          <Select
            onChange={(e) => {
              setCitizenship(e);
            }}
            placeholder="Гражданство"
            allowClear
          >
            {citizenshipList.map(({ value, text }) => (
              <Select.Option key={value} value={value}>
                {text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {citizenship !== 'resident' && (
          <Form.Item
            name="country_of_residency"
            label="Страна"
            rules={[requiredRule]}
          >
            <Select placeholder="Страна" allowClear showSearch>
              {countries.map(({ code, name }) => (
                <Select.Option title={name} key={code} value={code}>
                  {name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        )}

        <Form.Item name="disability" label="Инвалидность">
          <Select placeholder="Инвалидность" allowClear>
            {disabilityList.map(({ value, text }) => (
              <Select.Option key={value} value={value}>
                {text}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Вычет МЗП"
          name="tax_deduction"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item label="Пенсионер" name="pensioner" valuePropName="checked">
          <Switch onChange={(value) => showPensionerField(value)} />
        </Form.Item>

        {isOpenPensionerField && (
          <Form.Item
            name="pensioner_date"
            label="Дата выхода на пенсию"
            rules={[requiredRule]}
          >
            <DatePicker
              format="DD.MM.YYYY"
              allowClear
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        <Form.Item
          label="Вычет ВОСМС для ИПН"
          name="vsms_substract_ipn"
          valuePropName="checked"
        >
          <Switch disabled />
        </Form.Item>

        <Form.Item wrapperCol={{ offset: CONTENT_SPAN }}>
          <Button type="primary" htmlType="submit">
            Сохранить
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}

export { EmployeesForm };
